import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Logo = styled.a`
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  color: var(--dark-blue);
  text-decoration: none;
  
  span {
    color: var(--primary-blue);
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: var(--dark-blue);
  font-weight: 600;
  text-decoration: none;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--primary-blue);
    transition: width 0.3s ease;
  }
  
  &:hover:after, &.active:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background-color: var(--white);
  padding: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  z-index: 999;
`;

const MobileNavLink = styled.a`
  color: var(--dark-blue);
  font-weight: 600;
  text-decoration: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--light-gray);
  
  &:last-child {
    border-bottom: none;
  }
`;

const Hamburger = styled.div`
  width: 24px;
  height: 20px;
  position: relative;
  
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--dark-blue);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;
    
    &:nth-child(1) {
      top: 0px;
    }
    
    &:nth-child(2), &:nth-child(3) {
      top: 9px;
    }
    
    &:nth-child(4) {
      top: 18px;
    }
  }
  
  &.open {
    span {
      &:nth-child(1) {
        top: 9px;
        width: 0%;
        left: 50%;
      }
      
      &:nth-child(2) {
        transform: rotate(45deg);
      }
      
      &:nth-child(3) {
        transform: rotate(-45deg);
      }
      
      &:nth-child(4) {
        top: 9px;
        width: 0%;
        left: 50%;
      }
    }
  }
`;

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section');
      const scrollPosition = window.scrollY + 100;
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id') || '';
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
      
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  const closeMenu = () => {
    setIsOpen(false);
  };
  
  return (
    <Nav style={{ backgroundColor: scrolled ? 'var(--white)' : 'transparent', boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none' }}>
      <NavContainer>
        <Logo href="#home">
          Karen<span>Lorca</span>
        </Logo>
        
        <NavLinks>
          <NavLink href="#home" className={activeSection === 'home' ? 'active' : ''}>
            Home
          </NavLink>
          <NavLink href="#about" className={activeSection === 'about' ? 'active' : ''}>
            About
          </NavLink>
          <NavLink href="#proficiency" className={activeSection === 'proficiency' ? 'active' : ''}>
            Proficiency
          </NavLink>
          <NavLink href="#skills" className={activeSection === 'skills' ? 'active' : ''}>
            Skills
          </NavLink>
          <NavLink href="#ehr-platforms" className={activeSection === 'ehr-platforms' ? 'active' : ''}>
            EHR/EMR
          </NavLink>
          <NavLink href="#software-platforms" className={activeSection === 'software-platforms' ? 'active' : ''}>
            Software
          </NavLink>
          <NavLink href="#certificates" className={activeSection === 'certificates' ? 'active' : ''}>
            Certificates
          </NavLink>
          <NavLink href="#experience" className={activeSection === 'experience' ? 'active' : ''}>
            Experience
          </NavLink>
          <NavLink href="#contact" className={activeSection === 'contact' ? 'active' : ''}>
            Contact
          </NavLink>
        </NavLinks>
        
        <MobileMenuButton onClick={toggleMenu} aria-label="Toggle menu">
          <Hamburger className={isOpen ? 'open' : ''}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
        </MobileMenuButton>
      </NavContainer>
      
      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavLink href="#home" onClick={closeMenu}>Home</MobileNavLink>
            <MobileNavLink href="#about" onClick={closeMenu}>About</MobileNavLink>
            <MobileNavLink href="#proficiency" onClick={closeMenu}>Proficiency</MobileNavLink>
            <MobileNavLink href="#skills" onClick={closeMenu}>Skills</MobileNavLink>
            <MobileNavLink href="#ehr-platforms" onClick={closeMenu}>EHR/EMR</MobileNavLink>
            <MobileNavLink href="#software-platforms" onClick={closeMenu}>Software</MobileNavLink>
            <MobileNavLink href="#certificates" onClick={closeMenu}>Certificates</MobileNavLink>
            <MobileNavLink href="#experience" onClick={closeMenu}>Experience</MobileNavLink>
            <MobileNavLink href="#contact" onClick={closeMenu}>Contact</MobileNavLink>
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
};

export default Navbar;
